import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LogoMark from '../LogoMark';

const APP_STORE_URL  = 'https://apps.apple.com/us/app/fundpot/id6776429234';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.aramsarkisyan.FundPotApp';

const s = {
  page: { minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' },
  wrap: { width: '100%', maxWidth: 1080, padding: '0 24px', position: 'relative', zIndex: 1 },

  // Top nav
  nav: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 28 },
  navBrand: { display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none', color: 'var(--text)' },
  navWordmark: { fontSize: 21, fontWeight: 800, letterSpacing: -0.6 },
  navLink: { color: 'var(--subtext)', textDecoration: 'none', fontSize: 14.5, fontWeight: 600 },

  // Hero
  tagline: { fontSize: 18.5, color: 'var(--subtext)', lineHeight: 1.65, maxWidth: 520 },
  ctaRow: { display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginTop: 34 },
  ctaGhost: {
    background: 'transparent', color: 'var(--text)', border: '1px solid var(--border)',
    borderRadius: 14, padding: '16px 28px', fontSize: 15.5, fontWeight: 700,
    textDecoration: 'none', display: 'inline-block',
  },
  trustRow: { display: 'flex', gap: 20, flexWrap: 'wrap', marginTop: 30, color: 'var(--subtext)', fontSize: 14 },
  trustItem: { display: 'flex', alignItems: 'center', gap: 7 },
  dot: { width: 6, height: 6, borderRadius: 3, background: 'var(--accent)', flexShrink: 0 },

  // Sections
  section: { padding: '68px 0', borderTop: '1px solid var(--border)' },
  sectionTitle: { fontSize: 'clamp(28px, 3.6vw, 36px)', fontWeight: 800, letterSpacing: -1.1, marginBottom: 10, textAlign: 'center' },
  sectionSub: { fontSize: 16.5, color: 'var(--subtext)', textAlign: 'center', maxWidth: 560, margin: '0 auto 48px', lineHeight: 1.65 },

  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 20 },
  cardItem: { borderRadius: 22, padding: 32 },
  stepNum: {
    width: 42, height: 42, borderRadius: 13,
    background: 'linear-gradient(135deg, #2BE3A4 0%, #0FA57A 100%)', color: '#04140F',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 17, marginBottom: 20,
    boxShadow: '0 8px 22px rgba(43,227,164,0.25)',
  },
  cardHead: { fontSize: 18, fontWeight: 800, marginBottom: 9, letterSpacing: -0.3 },
  cardBody: { fontSize: 15, color: 'var(--subtext)', lineHeight: 1.65 },

  // Fees
  feeBox: { borderRadius: 24, padding: '14px 34px', maxWidth: 660, margin: '0 auto' },
  feeRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '19px 0', borderBottom: '1px solid var(--border)' },
  feeLabel: { fontSize: 15.5, color: 'var(--text)', fontWeight: 700 },
  feeSub: { fontSize: 13.5, color: 'var(--subtext)', marginTop: 4, lineHeight: 1.5 },
  feeVal: { fontSize: 15.5, fontWeight: 800, whiteSpace: 'nowrap', marginLeft: 18, color: 'var(--accent)' },
  feeValGold: { fontSize: 15.5, fontWeight: 800, whiteSpace: 'nowrap', marginLeft: 18, color: 'var(--gold)' },

  // Fund lookup
  lookup: { borderRadius: 26, padding: 38, maxWidth: 460, margin: '0 auto', boxShadow: '0 24px 70px rgba(0,0,0,0.45)' },
  label: { fontSize: 12.5, fontWeight: 800, color: 'var(--subtext)', textTransform: 'uppercase', letterSpacing: 1.4, marginBottom: 10, display: 'block' },
  input: {
    width: '100%', background: 'var(--inputBg)', border: '1px solid var(--border)', borderRadius: 14,
    padding: '17px 18px', color: 'var(--text)', fontSize: 22, fontWeight: 800, letterSpacing: 4,
    textAlign: 'center', textTransform: 'uppercase', outline: 'none', fontFamily: 'inherit', marginBottom: 16,
  },
  btn: {
    width: '100%', background: 'linear-gradient(135deg, #2BE3A4 0%, #12BD87 100%)', color: '#04140F',
    border: 'none', borderRadius: 14, padding: 17, fontSize: 16, fontWeight: 800, cursor: 'pointer',
    fontFamily: 'inherit', boxShadow: '0 10px 28px rgba(43,227,164,0.3)',
  },
  btnDisabled: { opacity: 0.4, cursor: 'not-allowed', boxShadow: 'none' },
  err: { color: 'var(--danger)', fontSize: 14, textAlign: 'center', marginTop: 12 },

  // Closing CTA band
  ctaBand: { borderRadius: 28, padding: '56px 40px', textAlign: 'center', maxWidth: 760, margin: '0 auto' },
  ctaBandTitle: { fontSize: 'clamp(26px, 3.4vw, 34px)', fontWeight: 800, letterSpacing: -1, marginBottom: 12 },
  ctaBandSub: { fontSize: 16, color: 'var(--subtext)', marginBottom: 30, lineHeight: 1.6 },

  footer: { borderTop: '1px solid var(--border)', padding: '44px 24px', textAlign: 'center', color: 'var(--subtext)', fontSize: 14, width: '100%', position: 'relative', zIndex: 1 },
  footerLink: { color: 'var(--accent)', textDecoration: 'none', margin: '0 8px' },
};

function AppStoreBadge({ height = 56 }) {
  return (
    <a className="badge-apple" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
      <img src="/appstore-badge.svg" alt="Download on the App Store" style={{ height }} />
    </a>
  );
}

// The badge PNG is pre-cropped to its artwork (Google ships it with 16%
// transparent padding on every side), so it takes the same height as the Apple
// badge and lines up without any negative-margin fudging.
function GooglePlayBadge({ height = 56 }) {
  return (
    <a className="play-badge" href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center' }}>
      <img src="/google-play-badge-trimmed.png" alt="Get it on Google Play" style={{ height, display: 'block' }} />
    </a>
  );
}

function Step({ n, head, children, delay }) {
  return (
    <div className="lift fade-up glass" style={{ ...s.cardItem, animationDelay: `${delay}s` }}>
      <div style={s.stepNum}>{n}</div>
      <div style={s.cardHead}>{head}</div>
      <div style={s.cardBody}>{children}</div>
    </div>
  );
}

function Shot({ img, alt, caption, sub, delay }) {
  return (
    <div className="shot-card fade-up" style={{ animationDelay: `${delay}s` }}>
      <div className="phone">
        <img src={img} alt={alt} loading="lazy" />
      </div>
      <div className="shot-caption">{caption}</div>
      <div className="shot-sub">{sub}</div>
    </div>
  );
}

export default function Home() {
  const [code, setCode] = useState('');
  const [err, setErr]   = useState('');
  const navigate        = useNavigate();

  function handleSubmit(e) {
    e.preventDefault();
    const clean = code.trim().toUpperCase();
    if (clean.length < 4) { setErr('Enter the fund code (e.g. TRIP2026).'); return; }
    navigate(`/fund/${clean}`);
  }

  const ready = code.trim().length >= 4;

  return (
    <div style={s.page}>
      <div className="mesh" />
      <div style={s.wrap}>

        {/* Nav */}
        <nav style={s.nav} className="fade-up">
          <a style={s.navBrand} href="/">
            <LogoMark size={42} />
            <span style={s.navWordmark}>FundPot</span>
          </a>
          <a style={s.navLink} href="mailto:aram@sarklabs.ai">Contact</a>
        </nav>

        {/* Hero */}
        <header className="hero-grid">
          <div>
            <div className="eyebrow fade-up">Now on iPhone &amp; Android</div>
            <h1 className="hero-h1 fade-up" style={{ animationDelay: '0.06s' }}>
              One pot.<br />
              <span className="gradient-text">Everyone chips&nbsp;in.</span>
            </h1>
            <p className="fade-up" style={{ ...s.tagline, animationDelay: '0.12s' }}>
              Private group funds for friends and family. Pool money toward a shared
              goal — a trip, a BBQ, a group gift — and track every dollar in one place.
            </p>
            <div className="fade-up hero-ctas" style={{ ...s.ctaRow, animationDelay: '0.18s' }}>
              {/* Both badges live in one nowrap group so they always sit side by
                  side — otherwise the three CTAs wrap unevenly on narrow screens
                  and App Store ends up stranded on its own line. */}
              <span className="store-badges">
                <AppStoreBadge />
                <GooglePlayBadge />
              </span>
              <a className="cta-ghost" style={s.ctaGhost} href="#lookup">View a fund</a>
            </div>
            <div className="fade-up hero-trust" style={{ ...s.trustRow, animationDelay: '0.26s' }}>
              <span style={s.trustItem}><span style={s.dot} /> Invite-only</span>
              <span style={s.trustItem}><span style={s.dot} /> We never hold your money</span>
              <span style={s.trustItem}><span style={s.dot} /> Powered by Stripe &amp; Plaid</span>
            </div>
          </div>
          <div className="hero-phone-wrap fade-up" style={{ animationDelay: '0.2s' }}>
            <div className="phone hero-phone" style={{ maxWidth: 310 }}>
              <img src="/screens/shot2.png" alt="FundPot home screen showing three active funds" />
            </div>
          </div>
        </header>

        {/* Fund lookup */}
        <section style={s.section} id="lookup">
          <h2 className="fade-up" style={s.sectionTitle}>View a fund</h2>
          <p className="fade-up" style={{ ...s.sectionSub, animationDelay: '0.05s' }}>Have a fund code? Enter it to see the balance and contribute.</p>
          <div className="fade-up glass" style={{ ...s.lookup, animationDelay: '0.1s' }}>
            <form onSubmit={handleSubmit}>
              <label style={s.label}>Fund Code</label>
              <input
                className="field"
                style={s.input}
                placeholder="TRIP2026"
                value={code}
                onChange={e => { setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '')); setErr(''); }}
                maxLength={14}
                autoCapitalize="characters"
                spellCheck={false}
              />
              <button
                className={ready ? 'cta-primary' : ''}
                style={{ ...s.btn, ...(ready ? {} : s.btnDisabled) }}
                type="submit"
                disabled={!ready}
              >
                View Fund →
              </button>
              {err && <p style={s.err}>{err}</p>}
            </form>
          </div>
        </section>

        {/* How it works */}
        <section style={s.section}>
          <h2 className="fade-up" style={s.sectionTitle}>How it works</h2>
          <p className="fade-up" style={{ ...s.sectionSub, animationDelay: '0.05s' }}>
            FundPot is a software tool for closed, invite-only groups. It is not
            public crowdfunding and never takes custody of your money.
          </p>
          <div style={s.grid}>
            <Step n="1" head="Create a fund" delay={0.05}>
              An organizer starts a private fund for a shared goal and invites
              friends or family with a short code.
            </Step>
            <Step n="2" head="Members contribute" delay={0.12}>
              Members chip in by card, Apple&nbsp;Pay, or Zelle. Card payments are
              processed by Stripe and go straight to the organizer's own connected
              Stripe account.
            </Step>
            <Step n="3" head="Organizer pays out" delay={0.19}>
              When the goal is met, the organizer pays the pooled funds out to their
              own bank. FundPot never holds the money.
            </Step>
          </div>
        </section>

        {/* Screenshots */}
        <section style={s.section}>
          <h2 className="fade-up" style={s.sectionTitle}>Built for the group chat era</h2>
          <p className="fade-up" style={{ ...s.sectionSub, animationDelay: '0.05s' }}>
            Everything your group needs to hit the goal — without the "who paid?"
            spreadsheet.
          </p>
          <div className="shots-grid">
            <Shot
              img="/screens/shot1.png"
              alt="Fund overview showing total collected and progress toward the goal"
              caption="Track every dollar"
              sub="Live balance, goal progress, and a join code your whole group can use."
              delay={0.05}
            />
            <Shot
              img="/screens/shot3.png"
              alt="Contribution screen with Apple Pay, card, and Zelle options"
              caption="Pay how you want"
              sub="Apple Pay, card, or Zelle — fees are always shown before you confirm."
              delay={0.12}
            />
            <Shot
              img="/screens/shot5.png"
              alt="Group chat with a poll deciding which hotel to book"
              caption="Decide together"
              sub="Built-in group chat with polls, so the plan and the money live in one place."
              delay={0.19}
            />
          </div>
        </section>

        {/* Fees */}
        <section style={s.section}>
          <h2 className="fade-up" style={s.sectionTitle}>Transparent fees</h2>
          <p className="fade-up" style={{ ...s.sectionSub, animationDelay: '0.05s' }}>
            Creating and joining funds is free. Fees are always shown before you
            confirm anything.
          </p>
          <div className="fade-up glass" style={{ ...s.feeBox, animationDelay: '0.1s' }}>
            <div style={s.feeRow}>
              <div>
                <div style={s.feeLabel}>Zelle contributions</div>
                <div style={s.feeSub}>Paid directly to the organizer</div>
              </div>
              <div style={s.feeVal}>Free</div>
            </div>
            <div style={s.feeRow}>
              <div>
                <div style={s.feeLabel}>Card / Apple Pay contributions</div>
                <div style={s.feeSub}>Processing &amp; service fee, shown before you pay — the fund receives the full contribution amount</div>
              </div>
              <div style={s.feeValGold}>3.9% + $0.50</div>
            </div>
            <div style={s.feeRow}>
              <div>
                <div style={s.feeLabel}>Standard payout</div>
                <div style={s.feeSub}>To the organizer's bank, ~2 business days</div>
              </div>
              <div style={s.feeVal}>Free</div>
            </div>
            <div style={s.feeRow}>
              <div>
                <div style={s.feeLabel}>Instant payout</div>
                <div style={s.feeSub}>To the organizer's debit card, within minutes</div>
              </div>
              <div style={s.feeValGold}>2%</div>
            </div>
            <div style={{ ...s.feeRow, borderBottom: 'none' }}>
              <div>
                <div style={s.feeLabel}>Automatic Zelle detection</div>
                <div style={s.feeSub}>Optional for organizers — Zelle deposits confirm themselves</div>
              </div>
              <div style={s.feeValGold}>$2.99 / 6 mo</div>
            </div>
          </div>
        </section>

        {/* Closing CTA */}
        <section style={s.section}>
          <div className="fade-up glass" style={s.ctaBand}>
            <h2 style={s.ctaBandTitle}>
              Start your first <span className="gold-text">pot</span> tonight
            </h2>
            <p style={s.ctaBandSub}>
              Free to download. Free to create a fund. Your group's next trip,
              gift, or league pot starts with one code.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
              <AppStoreBadge height={60} />
              <GooglePlayBadge height={60} />
            </div>
          </div>
        </section>
      </div>

      <footer style={s.footer}>
        <div style={{ marginBottom: 10 }}>
          <a style={s.footerLink} href="/terms.html">Terms of Service</a>
          <a style={s.footerLink} href="/privacy.html">Privacy Policy</a>
          <a style={s.footerLink} href="mailto:aram@sarklabs.ai">Contact</a>
        </div>
        <div>© {new Date().getFullYear()} Sark Labs LLC. All rights reserved.</div>
        <div style={{ marginTop: 6, fontSize: 13, opacity: 0.8 }}>
          FundPot is a product of Sark Labs LLC · A software platform for private group funds
        </div>
      </footer>
    </div>
  );
}
